import React from "react";
import { Star } from "lucide-react";
import { assetUrl } from "../../lib/api";
import { TeamBadge } from "../TeamBadge";
import { TEAM_INFO } from "../../lib/teamMap";

const initials = (s = "") => {
    const p = s.trim().split(/\s+/).filter(Boolean);
    return (p.length > 1 ? p[0][0] + p[p.length - 1][0] : s.slice(0, 2)).toUpperCase();
};
const teamName = (id) => TEAM_INFO[id]?.name || (id || "").toUpperCase();

// Small player chip — the "subjects need to be small" requirement.
function Mini({ subject, color, px = 46, label }) {
    const src = subject?.photo_url ? assetUrl(subject.photo_url) : null;
    return (
        <div className="flex flex-col items-center gap-1 min-w-0">
            <div className="rounded-full p-[2px]" style={{ background: `linear-gradient(160deg, ${color}, ${color}44)`, width: px + 4, height: px + 4 }}>
                <div className="rounded-full overflow-hidden bg-[#0E1110] flex items-center justify-center w-full h-full">
                    {src ? <img src={src} alt="" className="w-full h-full object-cover object-top" loading="lazy" />
                        : subject?.team_id ? <TeamBadge teamId={subject.team_id} size="sm" asLink={false} />
                            : <span className="font-story-punch text-sm text-white/60">{initials(subject?.name)}</span>}
                </div>
            </div>
            {label && <span className="text-[8px] font-black uppercase tracking-[0.14em]" style={{ color }}>{label}</span>}
            <span className="text-[9px] font-bold text-white/85 text-center leading-[1.05] truncate max-w-[80px]">{subject?.name || "—"}</span>
        </div>
    );
}

const shell = (color) => ({
    background: `radial-gradient(120% 130% at 82% 6%, ${color}30 0%, transparent 52%), linear-gradient(162deg, #101114 0%, #07080B 100%)`,
});

function Frame({ testId, color, children, crumb }) {
    return (
        <article data-testid={testId} data-orientation="v"
            className="relative overflow-hidden rounded-2xl border border-white/10 aspect-[4/5] flex flex-col p-4 shadow-lg shadow-black/50"
            style={shell(color)}>
            {children}
            <div className="relative mt-auto pt-2.5 flex items-center gap-2 border-t border-white/10">
                {crumb}
                <span className="ml-auto text-[9px] font-black uppercase tracking-[0.24em] text-white/35">ROSTERLY.COM</span>
            </div>
        </article>
    );
}

// STARTING LINEUP — 3 forwards / 2 defence / 1 goalie, small subjects.
function LineupPoster({ s, testId }) {
    const color = s.teamColor || "#9D4CDD";
    const lu = Array.isArray(s.lineup) ? s.lineup : [];
    const F = lu.slice(0, 3), D = lu.slice(3, 5), G = lu[5];
    const Row = ({ title, items }) => (
        <div className="relative">
            <div className="flex items-center gap-2 mb-1.5">
                <span className="text-[8px] font-black uppercase tracking-[0.28em]" style={{ color }}>{title}</span>
                <span className="h-px flex-1 bg-white/10" />
            </div>
            <div className="flex items-start justify-center gap-4">
                {items.map((it, i) => <Mini key={i} subject={it?.subject || it} color={color} px={it?.pos === "G" ? 58 : 48} label={it?.pos} />)}
            </div>
        </div>
    );
    return (
        <Frame testId={testId} color={color} crumb={<><TeamBadge teamId={s.teamId} size="xs" asLink={false} /><span className="text-[10px] font-black uppercase tracking-wider text-white/75 truncate">{s.when || "Tonight"}{s.oppId ? ` · vs ${(s.oppId || "").toUpperCase()}` : ""}</span></>}>
            {s.teamId && <div className="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 flex items-center justify-center opacity-[0.05]"><div style={{ transform: "scale(3.4)" }}><TeamBadge teamId={s.teamId} size="xl" asLink={false} /></div></div>}
            <div className="relative flex items-center gap-2 mb-3">
                {s.teamId && <TeamBadge teamId={s.teamId} size="sm" asLink={false} />}
                <div className="leading-none">
                    <div className="text-[9px] font-black uppercase tracking-[0.3em]" style={{ color }}>{teamName(s.teamId)}</div>
                    <h2 className="font-story-punch uppercase text-white text-3xl leading-[0.85]">Starting Lineup</h2>
                </div>
            </div>
            <div className="relative flex-1 flex flex-col justify-center gap-3">
                <Row title="Forwards" items={F} />
                <Row title="Defence" items={D} />
                <Row title="Goalie" items={G ? [G] : []} />
            </div>
        </Frame>
    );
}

// STARTING GOALIE — big-ish goalie, save line.
function GoaliePoster({ s, testId }) {
    const color = s.teamColor || "#9D4CDD";
    const g = s.subject;
    const src = g?.photo_url ? assetUrl(g.photo_url) : null;
    return (
        <Frame testId={testId} color={color} crumb={<><TeamBadge teamId={s.teamId} size="xs" asLink={false} /><span className="text-[10px] font-black uppercase tracking-wider text-white/75 truncate">{s.when || "Tonight"}{s.oppId ? ` · vs ${(s.oppId || "").toUpperCase()}` : ""}</span></>}>
            <div className="relative text-center">
                <div className="text-[10px] font-black uppercase tracking-[0.34em]" style={{ color }}>Starting In Goal</div>
            </div>
            <div className="relative flex-1 flex items-center justify-center py-3">
                <div className="relative rounded-full p-[3px]" style={{ background: `linear-gradient(155deg, ${color}, ${color}33)`, width: 172, height: 172, boxShadow: `0 0 40px ${color}55` }}>
                    <div className="rounded-full overflow-hidden bg-[#0B0E13] flex items-center justify-center w-full h-full">
                        {src ? <img src={src} alt="" className="w-full h-full object-cover object-top" />
                            : g?.team_id ? <div style={{ transform: "scale(1.3)" }}><TeamBadge teamId={g.team_id} size="xl" asLink={false} /></div>
                                : <span className="font-story-punch text-4xl text-white/60">{initials(g?.name)}</span>}
                    </div>
                    <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 rounded-full font-story-punch text-white text-lg px-3 py-0.5" style={{ background: color }}>{s.number || "1"}</span>
                </div>
            </div>
            <div className="relative text-center">
                <h2 className="font-story-punch uppercase text-white leading-[0.85] text-4xl">{g?.name || "Goalie"}</h2>
                {s.statLine && <div className="mt-2 inline-flex items-center gap-2 rounded-md bg-white/5 border border-white/10 px-3 py-1.5"><span className="font-story-punch text-white text-sm tabular-nums">{s.statLine}</span></div>}
            </div>
        </Frame>
    );
}

// THREE STARS OF THE GAME — vertical, small faces, star ranks.
function StarsPoster({ s, testId }) {
    const color = s.teamColor || "#F5C518";
    const stars = Array.isArray(s.stars) ? s.stars.slice(0, 3) : [];
    return (
        <Frame testId={testId} color={color} crumb={<span className="text-[10px] font-black uppercase tracking-wider text-white/75 truncate">{s.matchup || (s.teamId && s.oppId ? `${(s.teamId || "").toUpperCase()} vs ${(s.oppId || "").toUpperCase()}` : "Postgame")}</span>}>
            <div className="relative mb-3">
                <div className="text-[9px] font-black uppercase tracking-[0.32em]" style={{ color }}>Postgame</div>
                <h2 className="font-story-punch uppercase text-white text-4xl leading-[0.82] flex items-center gap-2">3 Stars <Star className="h-6 w-6" style={{ color, fill: color }} /></h2>
            </div>
            <div className="relative flex-1 flex flex-col justify-center gap-2.5">
                {stars.map((st, i) => {
                    const sub = st.subject || st;
                    return (
                        <div key={i} className="flex items-center gap-3 rounded-xl bg-white/[0.03] border border-white/10 px-3 py-2">
                            <span className="font-story-punch text-3xl leading-none tabular-nums" style={{ color }}>{i + 1}</span>
                            <Mini subject={sub} color={color} px={44} />
                            <div className="min-w-0 flex-1">
                                <div className="text-xs font-black uppercase tracking-wide text-white truncate">{sub?.name}</div>
                                {st.line && <div className="text-[10px] text-white/55 font-bold uppercase tracking-wide">{st.line}</div>}
                            </div>
                            <TeamBadge teamId={sub?.team_id} size="xs" asLink={false} />
                        </div>
                    );
                })}
            </div>
        </Frame>
    );
}

// HAT TRICK — player + 3 pucks + score.
function HatTrickPoster({ s, testId }) {
    const color = s.teamColor || "#9D4CDD";
    const p = s.subject;
    const src = p?.photo_url ? assetUrl(p.photo_url) : null;
    return (
        <Frame testId={testId} color={color} crumb={<><TeamBadge teamId={s.teamId} size="xs" asLink={false} /><span className="text-[10px] font-black uppercase tracking-wider text-white/75 truncate">{s.matchup || (s.oppId ? `vs ${(s.oppId || "").toUpperCase()}` : "")}{s.score ? ` · ${s.score}` : ""}</span></>}>
            {s.teamId && <div className="pointer-events-none absolute -right-6 top-6 opacity-[0.06]"><div style={{ transform: "scale(3.2)" }}><TeamBadge teamId={s.teamId} size="xl" asLink={false} /></div></div>}
            <div className="relative">
                <div className="text-[9px] font-black uppercase tracking-[0.32em]" style={{ color }}>{teamName(s.teamId)}</div>
                <h2 className="font-story-punch uppercase text-white text-[3.4rem] leading-[0.8]">Hat<br />Trick</h2>
            </div>
            <div className="relative flex-1 flex items-center justify-center py-2">
                <div className="rounded-full p-[3px]" style={{ background: `linear-gradient(155deg, ${color}, ${color}33)`, width: 150, height: 150, boxShadow: `0 0 34px ${color}55` }}>
                    <div className="rounded-full overflow-hidden bg-[#0B0E13] flex items-center justify-center w-full h-full">
                        {src ? <img src={src} alt="" className="w-full h-full object-cover object-top" />
                            : p?.team_id ? <div style={{ transform: "scale(1.3)" }}><TeamBadge teamId={p.team_id} size="xl" asLink={false} /></div>
                                : <span className="font-story-punch text-3xl text-white/60">{initials(p?.name)}</span>}
                    </div>
                </div>
            </div>
            <div className="relative flex items-center justify-center gap-2 mb-2">
                {[0, 1, 2].map((i) => <span key={i} className="rounded-full" style={{ width: 16, height: 16, background: color, boxShadow: `0 0 10px ${color}` }} />)}
            </div>
            <div className="relative text-center">
                <h3 className="font-story-punch uppercase text-white text-2xl leading-none">{p?.name || "Player"}</h3>
            </div>
        </Frame>
    );
}

export function SchedulePoster({ s, layout, testId }) {
    if (layout === "lineup") return <LineupPoster s={s} testId={testId} />;
    if (layout === "goalie") return <GoaliePoster s={s} testId={testId} />;
    if (layout === "stars_v") return <StarsPoster s={s} testId={testId} />;
    if (layout === "hattrick_v") return <HatTrickPoster s={s} testId={testId} />;
    return null;
}

export const SCHEDULE_LAYOUTS = ["lineup", "goalie", "stars_v", "hattrick_v"];
